/**
 * custom-cart-attributes.js
 * CCC custom feature — syncs cart note-attributes to Shopify so they
 * appear in the admin "Additional details" panel on every order.
 *
 * When the cart contains:
 *  • Upcoming-roast coffee → sets upcoming_roast, roast_date, shipping_estimate
 *  • Preorder / partial-backorder items → sets preorder + preorder_dispatch_estimate
 *    (single detail line; preorder_items cleared so older carts drop the duplicate)
 *
 * Attributes are cleared when the relevant items are removed from the cart.
 *
 * Order note: keeps a "Roast requests:" section listing each Upcoming Roast
 * line of a coffee set to the roast we don't usually do (custom.roast_style_choice,
 * see custom-roast-style.js). Only that section is rewritten; whatever the
 * customer typed in the note box stays as they wrote it. The note box submits
 * with the checkout form, so the section is also re-applied on submit.
 *
 * Data source: reads the Liquid-rendered JSON element
 *   <script type="application/json" data-ccc-cart-state>
 * which is output by cart-products.liquid (present in both the cart page
 * and the cart drawer).
 *
 * Loaded on the cart page via sections/main-cart.liquid.
 */

import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { cachedRoastDate, getShippingRange } from '@theme/custom-roast-manager';
import { getAdminDispatchEstimate, getDispatchKey } from '@theme/custom-dispatch-manager';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Writes cart note-attributes (and the order note, when given) via Shopify's AJAX API.
 * @param {Record<string, string>} attributes
 * @param {string} [note]
 */
async function updateCartAttributes(attributes, note) {
  const payload = note === undefined ? { attributes } : { attributes, note };
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`/cart/update.js ${res.status}`);
  return res.json();
}

/**
 * Read the Liquid-rendered cart state JSON from the DOM.
 * cart-products.liquid outputs this in both cart page and cart drawer.
 * @returns {{ hasCoffee: boolean, hasPreorders: boolean, preorderItems: Array<{name: string, type: string, vendor: string, estimatedRestockDate?: string|null}> } | null}
 */
function readCartState() {
  const el = document.querySelector('script[data-ccc-cart-state]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[CCC] Failed to parse cart state JSON', e);
    return null;
  }
}

/**
 * Read the attributes ALREADY stored on the cart, rendered server-side by
 * cart-products.liquid. Lets us skip a redundant /cart/update.js write on load —
 * that write is intercepted by Shopify's cart events and forces a full section
 * re-fetch + morph, which re-renders the totals and resets every custom badge to
 * its placeholder (the visible "flash on load"). If the cart already carries the
 * attributes we'd set, there's nothing to write and no reason to morph.
 * @returns {Record<string, string> | null}
 */
function readCurrentCartAttributes() {
  const el = document.querySelector('script[data-ccc-cart-attrs]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[CCC] Failed to parse current cart attributes JSON', e);
    return null;
  }
}

// ─── Order note: roast requests ─────────────────────────────────────────────

const ROAST_REQUESTS_HEADER = 'Roast requests:';
// The header line plus the "• " lines under it. Anything else in the note is the customer's.
const ROAST_REQUESTS_RE = /(^|\n)[^\S\n]*Roast requests:[^\S\n]*(?:\n• [^\n]*)*/g;

/**
 * The note with the roast requests section rebuilt from the cart lines.
 * @param {string} note - Current note (customer text, maybe an old section)
 * @param {Array<{title: string, size: string, quantity: number, style: string}>} lines
 * @returns {string}
 */
function buildNoteWithRoastRequests(note, lines) {
  const customerText = (note || '').replace(ROAST_REQUESTS_RE, '').trimEnd();
  if (!lines || lines.length === 0) return customerText;

  const block = [
    ROAST_REQUESTS_HEADER,
    ...lines.map((line) => {
      const size = line.size ? `, ${line.size}` : '';
      return `• ${line.title}${size} × ${line.quantity}: ${line.style} roast`;
    }),
  ].join('\n');
  return customerText ? `${customerText}\n\n${block}` : block;
}

/** @returns {HTMLTextAreaElement[]} The cart note boxes (cart page and/or drawer). */
function noteTextareas() {
  return /** @type {HTMLTextAreaElement[]} */ (Array.from(document.querySelectorAll('textarea[name="note"]')));
}

/** True after the customer edits the note box, until the next sync writes the note. */
let _noteDirty = false;

// ─── Main sync function ─────────────────────────────────────────────────────

/** Track last synced JSON to avoid redundant /cart/update.js calls. */
let _lastSyncedJSON = '';

/**
 * Read the cart state from Liquid JSON, build the attribute map, and POST it.
 */
async function syncCartAttributes() {
  try {
    const state = readCartState();
    if (!state) return; // cart-products not rendered yet

    // Cart roast pills (<ccc-cart-roast-style>) can arrive by morph, which never
    // runs a <script>, so load their element definition from here.
    if (state.hasRoastChoice || state.roastStyleLines?.length) {
      import('@theme/custom-roast-style').catch((e) => console.warn('[CCC] Roast style UI failed to load', e));
    }

    const attrs = {};

    // ── 1. Coffee (upcoming roast) ──────────────────────────────────────
    const roastInfo = state.hasCoffee ? await cachedRoastDate() : null;
    if (state.hasCoffee) {
      const info = roastInfo;
      attrs.upcoming_roast = 'true';

      if (info) {
        attrs.roast_date = info.formatted;
        attrs.shipping_estimate = getShippingRange(info.roastDate);
      } else {
        attrs.roast_date = 'next roast date';
        attrs.shipping_estimate = 'after roasting';
      }
    } else {
      attrs.upcoming_roast = '';
      attrs.roast_date = '';
      attrs.shipping_estimate = '';
    }

    // ── 2. Preorder / partial-backorder items ───────────────────────────
    if (state.hasPreorders && state.preorderItems.length > 0) {
      const dispatchParts = [];

      for (const item of state.preorderItems) {
        const name = item.name || 'Item';
        const lookupKey = getDispatchKey(item.type, item.vendor);
        const estimatedRestockDate = item.estimatedRestockDate || null;
        if (!lookupKey && !estimatedRestockDate) continue;

        const estimate = getAdminDispatchEstimate(lookupKey, { estimatedRestockDate });
        if (item.kind === 'partial_backorder') {
          const onHand = Number(item.onHand) || 0;
          const backordered = Number(item.backordered) || 0;
          dispatchParts.push(
            `${name}: ${onHand} in stock + ${backordered} backorder (${estimate})`
          );
        } else {
          dispatchParts.push(`${name}: ${estimate}`);
        }
      }

      attrs.preorder = 'true';
      // Clear legacy duplicate attribute (names alone); detail line includes product names.
      attrs.preorder_items = '';
      attrs.preorder_dispatch_estimate = dispatchParts.join(' | ');
    } else {
      attrs.preorder = '';
      attrs.preorder_items = '';
      attrs.preorder_dispatch_estimate = '';
    }

    // ── 2b. Structured snapshot (hidden `_eta_snapshot` attribute) ──────
    // Machine-readable mirror of the human attributes above, read by the
    // order-insight customer account extension for exact date-moved
    // comparisons on the order page. Underscore prefix keeps it off
    // customer-facing surfaces and notification emails.
    const snapshotLines = state.hasPreorders
      ? state.preorderItems.map((item) => ({
          variantId: item.variantId || null,
          name: item.name || null,
          eta: item.estimatedRestockDate || null,
          kind: item.kind === 'partial_backorder' ? 'partial_backorder' : 'preorder',
        }))
      : [];
    let roastISO = null;
    if (state.hasCoffee && roastInfo && roastInfo.roastDate) {
      // roastDate is fake-UTC (Sydney fields in the UTC slots) — UTC getters only.
      const r = roastInfo.roastDate;
      roastISO = `${r.getUTCFullYear()}-${String(r.getUTCMonth() + 1).padStart(2, '0')}-${String(r.getUTCDate()).padStart(2, '0')}`;
    }
    attrs._eta_snapshot =
      roastISO || snapshotLines.length
        ? JSON.stringify({ roast: roastISO, lines: snapshotLines })
        : '';

    // ── 2c. Order note: roast requests section ──────────────────────────
    // The note box (when shown) holds the newest customer text and is what the
    // checkout form submits, so build from it; otherwise from the cart's note.
    const current = readCurrentCartAttributes();
    const serverNote = current?.note || '';
    const boxes = noteTextareas();
    const box = boxes.find((el) => el === document.activeElement) || boxes[0];
    const sourceNote = box ? box.value : serverNote;
    const desiredNote = buildNoteWithRoastRequests(sourceNote, state.roastStyleLines || []);
    const noteChanged = _noteDirty || desiredNote !== serverNote;

    // ── 3. Skip if nothing changed ──────────────────────────────────────
    const json = JSON.stringify({ attrs, note: desiredNote });
    if (json === _lastSyncedJSON && !_noteDirty) return;

    // ── 3b. Skip if the cart ALREADY carries these exact attributes ──────
    // The server-rendered attributes are the source of truth on load. If they
    // already match what we'd write, POSTing /cart/update.js would be a no-op
    // change — except Shopify's cart events still turn it into a full section
    // re-fetch + morph, which is exactly the on-load flash we're removing. So
    // record the state as synced and return without touching the network.
    if (current && !noteChanged) {
      const alreadyMatches = Object.keys(attrs).every(
        (key) => (current[key] || '') === (attrs[key] || '')
      );
      if (alreadyMatches) {
        _lastSyncedJSON = json;
        return;
      }
    }

    // ── 4. Write to Shopify ─────────────────────────────────────────────
    _noteDirty = false;
    await updateCartAttributes(attrs, noteChanged ? desiredNote : undefined);
    _lastSyncedJSON = json;

    // The note box skips morph updates (data-skip-node-update), so show the
    // rebuilt section there ourselves. Never while the customer is typing in it.
    if (noteChanged) {
      for (const box of noteTextareas()) {
        if (box !== document.activeElement && box.value !== desiredNote) box.value = desiredNote;
      }
    }

  } catch (err) {
    console.warn('[CCC] Cart attributes sync failed:', err);
  }
}

// ─── Event wiring ───────────────────────────────────────────────────────────

// Run on initial page load. Delay 150ms to let Liquid/morph finish
// populating the DOM (cart-products JSON must be rendered first).
setTimeout(syncCartAttributes, 150);

// Horizon 4.2.0: the cart section is re-rendered on a browser Back without a
// cartLinesUpdate; the event fires after that morph has finished.
document.addEventListener(ThemeEvents.cartSectionRestored, () => syncCartAttributes());

// Order note box: Horizon's cart-note.js saves whatever is in the box, so if the
// customer edits or deletes the roast requests section, put it back once they
// pause typing, and straight away when they leave the box.
let _noteTimer = 0;
document.addEventListener('input', (event) => {
  if (!(event.target instanceof HTMLTextAreaElement) || event.target.name !== 'note') return;
  _noteDirty = true;
  clearTimeout(_noteTimer);
  _noteTimer = window.setTimeout(syncCartAttributes, 1500);
});
document.addEventListener('focusout', (event) => {
  const box = event.target;
  if (!(box instanceof HTMLTextAreaElement) || box.name !== 'note') return;
  if (_noteDirty) {
    clearTimeout(_noteTimer);
    _noteTimer = window.setTimeout(syncCartAttributes, 0);
    return;
  }
  // Already saved while they were typing, but the box was left alone then.
  const state = readCartState();
  if (!state) return;
  const desired = buildNoteWithRoastRequests(box.value, state.roastStyleLines || []);
  if (box.value !== desired) box.value = desired;
});

// The note box belongs to the checkout form, so its text is what reaches the
// order. Make sure the roast requests section is in it at that moment.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
    const state = readCartState();
    if (!state) return;
    for (const box of noteTextareas()) {
      if (box.form !== form) continue;
      box.value = buildNoteWithRoastRequests(box.value, state.roastStyleLines || []);
    }
  },
  true
);

// Re-sync after cart morph completes (event.promise resolves after morphSection).
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(() => syncCartAttributes())
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[CCC] Cart attributes sync skipped:', error);
    });
});
